/**
 * Briefing (08 §2.3): the operations order typed out on the left; the mission card, the intel on
 * offer and a schematic of the area on the right; ACCEPT / BACK. The schematic shows objective
 * AREAS only - that is the fidelity the intel allows; structures, patrols and minefields are
 * never drawn here. Buying a card re-reads the node: RP, the card and the order all follow.
 */

import './briefing.css';
import type { Deployment } from '../../../meta/api';
import { practiceParams } from '../../../meta/dogs';
import type { Ctx, MissionPlan, Screen } from '../context';
import { confirmDailyFlight } from '../daily/oneAttempt';
import { box, button, el, errorMessage, panel, show, Timers } from '../dom';
import { frame } from '../widgets/frame';
import type { KeyHint } from '../widgets/keyHints';
import { NAV_HINTS } from '../widgets/keyHints';
import type { TypeSegment } from '../widgets/typewriter';
import { typewriter } from '../widgets/typewriter';
import type { BriefingSection } from './briefingText';
import { briefingText } from './briefingText';
import { groundOf } from './ground';
import { intelShop } from './intelShop';
import { areaStrip, missionCard, tagRecoveryName } from './missionCard';

const MODE_LABEL: Readonly<Record<MissionPlan['mode'], string>> = {
  operation: 'OPERATION',
  classic: 'CLASSIC - ORIGINAL RULES',
  practice: 'PRACTICE - NOTHING AT STAKE',
  daily: 'DAILY DROP - ONE ATTEMPT',
};

const HINTS: readonly KeyHint[] = [...NAV_HINTS, { keys: 'PGUP/PGDN', label: 'SCROLL THE ORDER' }];

/** The order as typewriter segments: each heading on its own styled line, then its paragraphs. Pure. */
export function orderSegments(sections: readonly BriefingSection[]): TypeSegment[] {
  const out: TypeSegment[] = [];
  sections.forEach((section, i) => {
    out.push({ text: `${i === 0 ? '' : '\n'}${section.heading}\n`, className: 'menu-type-head' });
    for (const line of section.lines) out.push({ text: `${line}\n` });
  });
  return out;
}

/** The plan as the roguelike layer holds it now: a bought card is on the node, not on our copy. */
function reread(ctx: Ctx, plan: MissionPlan): MissionPlan {
  const id = plan.node?.id;
  const node = id === undefined ? undefined : ctx.api.profile().operation?.nodes.find((n) => n.id === id);
  return node === undefined || node.params === null ? plan : { ...plan, node, params: node.params };
}

export function briefingScreen(ctx: Ctx, given: MissionPlan): Screen {
  // ROUND8: Practice flies the dog teams its rating brings (`practiceParams`, as `api.practice` does): the order,
  // the card and the ground must be read off the mission that will be flown, not the picker's bare choices.
  let plan = given.mode === 'practice' ? { ...given, params: practiceParams(given.params) } : given;
  const f = frame('briefing', 'BRIEFING', HINTS);
  const rp = el('span', '');
  f.info.append(el('span', '', MODE_LABEL[plan.mode]), rp);

  const order = panel('OPERATIONS ORDER', 'menu-grow menu-brief-order');
  // Focusable so Page Up / Page Down scroll the order from the keyboard.
  order.body.setAttribute('tabindex', '0');
  order.body.setAttribute('aria-label', 'OPERATIONS ORDER TEXT');
  // Its own timers, not `ctx.timers`: the router clears those when it swaps screens, which is
  // after this factory has run, and the first tick would be lost with them.
  const timers = new Timers();
  const cardHost = el('div', 'menu-brief-cardhost menu-col');
  const stripHost = el('div', 'menu-brief-striphost');

  /** `instant`: the order has been read once already; a bought card must not make him sit through it again. */
  const paint = (instant: boolean): void => {
    const name = tagRecoveryName(plan);
    const params = name === null ? plan.params : { ...plan.params, tagRecovery: { name } };
    timers.clear();
    const text = briefingText(params, ctx.describe, groundOf(plan.params));
    order.body.replaceChildren(typewriter(orderSegments(text), timers, { instant }).el);
    cardHost.replaceChildren(missionCard(ctx, plan));
    stripHost.replaceChildren(areaStrip(plan));
    const held = plan.mode === 'operation' ? ctx.api.profile().operation : null;
    rp.textContent = held === null ? '' : `RP ${held.rp}`;
  };

  const error = el('div', 'menu-error menu-hidden');
  error.setAttribute('role', 'alert');

  const back = (): void => {
    if (plan.mode === 'operation') ctx.nav.theatre();
    else if (plan.mode === 'classic') ctx.nav.classicMissions(plan.rangerId ?? '');
    else if (plan.mode === 'daily') ctx.nav.daily();
    else ctx.nav.practice();
  };

  const accept = (): void => {
    if (plan.mode === 'operation') {
      ctx.nav.rangerSelect(plan);
      return;
    }
    if (plan.mode === 'classic') {
      ctx.nav.loadout(plan);
      return;
    }
    const deploy = (): void => {
      let deployment: Deployment;
      try {
        deployment = plan.mode === 'daily' ? ctx.api.deployDaily(plan.dailyDate ?? '') : ctx.api.practice(plan.params);
      } catch (err) {
        error.textContent = errorMessage(err);
        show(error, true);
        return;
      }
      ctx.fly(deployment);
    };
    // The daily is spent the moment it is deployed: ask first. Practice costs nothing: just go.
    if (plan.mode === 'daily') confirmDailyFlight(ctx, false, deploy);
    else deploy();
  };

  const acceptButton = button(plan.mode === 'daily' ? 'FLY (ONE ATTEMPT)' : 'ACCEPT', accept);
  const schematic = panel('AREA - NORTH UP', 'menu-brief-area');
  schematic.body.append(
    box(
      'menu-brief-area-row',
      stripHost,
      // ROUND5: this said the detail "SHOWS ON THE TACTICAL MAP WITH THE MATCHING INTEL", and there is
      // no tactical map in the game (M7-report §Not built). What there is is the strip map in the
      // corner of the HUD, and since this round it does draw all three with the cards that buy them.
      el('div', 'menu-brief-caption menu-prose menu-dim menu-small', 'OBJECTIVE AREAS ONLY. STRUCTURES, PATROL'
        + ' ROUTES AND MINEFIELDS APPEAR ON THE STRIP MAP IN THE FIELD, WITH THE MATCHING INTEL.'),
    ),
  );
  const side = box('menu-col menu-brief-side', cardHost);
  // Intel is bought for a node of the theatre map: classic, practice and the daily have no shop.
  const node = plan.mode === 'operation' ? plan.node : null;
  if (node !== null) {
    const shop = intelShop(ctx, {
      nodeId: node.id,
      onBought: () => {
        plan = reread(ctx, plan);
        paint(true);
      },
      fallbackFocus: () => acceptButton,
    });
    side.append(shop.el);
  }
  side.append(schematic.panel, error, box('menu-row menu-brief-actions', acceptButton, button('BACK', back)));
  paint(false);
  f.body.append(order.panel, side);

  return {
    el: f.el,
    back,
    // Two inputs to deploy (08 §5.3): Enter accepts even while the order is still typing.
    initialFocus: () => acceptButton,
    dispose: () => timers.clear(),
  };
}
